import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { formatCurrency } from '../charts/d3/format';
import { GlassSurface } from '../ui/glass';
import { GlassButton } from '../ui/GlassButton';
import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { getCategoryColor } from '../../data/categoryColors';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  listTransactions,
  type ApiTransaction,
} from '../../services/transactionService';
import type { AnalyticsPeriod, CardSpendRow } from '../../types/analytics';
import type { SpendCategoryNode } from '../../types/financial';
import { formatCardTitle, formatDueDate, isDueSoon, moneyOrDash, formatAccountKind, isCreditAccount } from './creditCardFormat';
import { CardDueAlertButton } from './CardDueAlertButton';

type Props = {
  card: CardSpendRow | null;
  period: AnalyticsPeriod;
  periodKey: string;
  isLive: boolean;
  sampleCategories: SpendCategoryNode[];
  onClose: () => void;
  onOpenCategory: (slug: string, name: string, amount: number) => void;
  onViewAll: (card: CardSpendRow) => void;
};

type TxRow = {
  id: string;
  date: string;
  merchant: string;
  amount: number;
  direction: 'debit' | 'credit';
  categoryName?: string;
};

function formatShortDate(iso: string): string {
  const date = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function sampleRowsForCard(card: CardSpendRow, categories: SpendCategoryNode[]): TxRow[] {
  const slugs = new Set(card.categories.map((c) => c.categorySlug));
  const rows: TxRow[] = [];
  for (const category of categories) {
    if (!slugs.has(category.id)) continue;
    for (const merchant of category.merchants) {
      for (const tx of merchant.transactions) {
        rows.push({
          id: tx.id,
          date: tx.date,
          merchant: tx.merchant || merchant.name,
          amount: Math.abs(tx.amount),
          direction: 'debit',
          categoryName: category.name,
        });
      }
    }
  }
  return rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** Card-level drill-down: payment totals, categories, and transactions. */
export function CreditCardDetailModal({
  card,
  period,
  periodKey,
  isLive,
  sampleCategories,
  onClose,
  onOpenCategory,
  onViewAll,
}: Props) {
  const insets = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();
  const { tokens } = useAuth();
  const { view: householdView } = useHouseholdMembership();
  const [rows, setRows] = useState<TxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = card != null;
  const panelMaxHeight = Math.min(height - insets.top - insets.bottom - 48, 720);
  const listMaxHeight = Math.max(220, panelMaxHeight - 280);

  const mapTx = useCallback((tx: ApiTransaction): TxRow => {
    const dateRaw = tx.date as unknown;
    const dateStr =
      typeof dateRaw === 'string'
        ? dateRaw
        : dateRaw instanceof Date
          ? dateRaw.toISOString()
          : String(dateRaw);
    return {
      id: tx.id,
      date: dateStr.slice(0, 10),
      merchant: tx.merchant,
      amount: Math.abs(tx.amount),
      direction: tx.direction,
      categoryName: tx.categoryName,
    };
  }, []);

  useEffect(() => {
    if (!card) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (!isLive) {
      setRows(sampleRowsForCard(card, sampleCategories));
      setError(null);
      setLoading(false);
      return;
    }

    if (!tokens?.accessToken) {
      setRows([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void listTransactions({
      accessToken: tokens.accessToken,
      tab: 'spend',
      period,
      periodKey,
      spendAccountId: card.spendAccountId,
      view: householdView,
      limit: 40,
    })
      .then((response) => {
        if (cancelled) return;
        setRows(response.transactions.map(mapTx));
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [card, isLive, sampleCategories, tokens?.accessToken, period, periodKey, householdView, mapTx]);

  const dueLabel = formatDueDate(card?.paymentDueDate);
  const soon = isDueSoon(card?.paymentDueDate);
  const credit = isCreditAccount(card?.type);

  const categoryNodes = useMemo(
    () =>
      (card?.categories ?? []).map((row, index) => ({
        id: row.categorySlug,
        name: row.categoryName,
        amount: Math.abs(row.totalAmount),
        color: getCategoryColor(row.categorySlug, index),
        transactionCount: row.transactionCount,
      })),
    [card],
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={[
          styles.backdrop,
          {
            paddingTop: Math.max(insets.top, spacing.lg),
            paddingBottom: Math.max(insets.bottom, spacing.lg),
          },
        ]}
      >
        <Pressable
          style={styles.dismissArea}
          onPress={onClose}
          accessibilityLabel="Dismiss card details"
        />
        <View
          style={[
            styles.panelWrap,
            {
              maxWidth: Math.min(560, width - spacing.lg * 2),
              maxHeight: panelMaxHeight,
            },
          ]}
          pointerEvents="box-none"
        >
          <GlassSurface
            tint="neutral"
            rounded="xl"
            showHairline
            style={[styles.panel, { maxHeight: panelMaxHeight }]}
            contentStyle={styles.panelContent}
          >
            <View style={styles.header}>
              <View style={styles.headerText}>
                <Text style={styles.eyebrow}>{formatAccountKind(card?.type)}</Text>
                <Text style={styles.title}>{card ? formatCardTitle(card) : ''}</Text>
                {credit ? (
                  <View style={styles.dueRow}>
                    <Text style={[styles.sub, soon && styles.subSoon, styles.dueCopy]}>
                      {dueLabel ? `Due ${dueLabel}` : 'No due date on this statement'}
                      {card?.statementPeriod ? ` · ${card.statementPeriod}` : ''}
                    </Text>
                    {card ? <CardDueAlertButton card={card} /> : null}
                  </View>
                ) : (
                  <Text style={styles.sub}>
                    {`${card?.transactionCount ?? 0} transaction${
                      (card?.transactionCount ?? 0) === 1 ? '' : 's'
                    } on this account`}
                    {card?.statementPeriod ? ` · ${card.statementPeriod}` : ''}
                  </Text>
                )}
              </View>
              <GlassButton variant="ghost" size="sm" onPress={onClose}>
                Close
              </GlassButton>
            </View>

            <View style={styles.kpis}>
              <View style={styles.kpi}>
                <Text style={styles.kpiLabel}>Spent</Text>
                <Text style={styles.kpiValue}>{formatCurrency(card?.totalSpend ?? 0)}</Text>
              </View>
              {credit ? (
                <>
                  <View style={styles.kpi}>
                    <Text style={styles.kpiLabel}>Pay in full</Text>
                    <Text style={[styles.kpiValue, styles.kpiDue]}>
                      {moneyOrDash(card?.statementBalance, formatCurrency)}
                    </Text>
                  </View>
                  <View style={styles.kpi}>
                    <Text style={styles.kpiLabel}>Minimum</Text>
                    <Text style={styles.kpiValue}>
                      {moneyOrDash(card?.minimumPayment, formatCurrency)}
                    </Text>
                  </View>
                </>
              ) : null}
            </View>

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <ScrollView
              style={{ maxHeight: listMaxHeight }}
              contentContainerStyle={styles.listContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
            >
              <Text style={styles.sectionTitle}>Categories</Text>
              {categoryNodes.length === 0 ? (
                <Text style={styles.empty}>No categorized spend on this card for the period.</Text>
              ) : (
                categoryNodes.map((category) => (
                  <Pressable
                    key={category.id}
                    accessibilityRole="button"
                    accessibilityLabel={`${category.name}, ${formatCurrency(category.amount)}. View transactions`}
                    onPress={() => onOpenCategory(category.id, category.name, category.amount)}
                    style={({ pressed }) => [styles.categoryRow, pressed && styles.rowPressed]}
                  >
                    <View style={[styles.swatch, { backgroundColor: category.color }]} />
                    <View style={styles.rowMain}>
                      <Text style={styles.merchant}>{category.name}</Text>
                      <Text style={styles.date}>
                        {category.transactionCount} transaction
                        {category.transactionCount === 1 ? '' : 's'}
                      </Text>
                    </View>
                    <Text style={styles.amount}>{formatCurrency(category.amount)}</Text>
                    <Text style={styles.chevron}>›</Text>
                  </Pressable>
                ))
              )}

              <View style={styles.txHeader}>
                <Text style={styles.sectionTitle}>Transactions</Text>
                {isLive && card ? (
                  <GlassButton variant="secondary" size="sm" onPress={() => onViewAll(card)}>
                    View all
                  </GlassButton>
                ) : null}
              </View>

              {loading ? (
                <ActivityIndicator color={colors.accent} style={styles.loader} />
              ) : rows.length === 0 ? (
                <Text style={styles.empty}>No transactions on this card for the period.</Text>
              ) : (
                rows.map((tx) => {
                  const signed =
                    tx.direction === 'credit'
                      ? formatCurrency(tx.amount)
                      : `−${formatCurrency(tx.amount)}`;
                  return (
                    <View key={tx.id} style={styles.row}>
                      <View style={styles.rowMain}>
                        <Text style={styles.merchant} numberOfLines={1}>
                          {tx.merchant}
                        </Text>
                        <Text style={styles.date}>
                          {formatShortDate(tx.date)}
                          {tx.categoryName ? ` · ${tx.categoryName}` : ''}
                        </Text>
                      </View>
                      <Text
                        style={[
                          styles.amount,
                          tx.direction === 'credit' && styles.amountCredit,
                        ]}
                      >
                        {signed}
                      </Text>
                    </View>
                  );
                })
              )}
            </ScrollView>
          </GlassSurface>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  dismissArea: {
    ...StyleSheet.absoluteFillObject,
  },
  panelWrap: {
    width: '100%',
    alignSelf: 'center',
  },
  panel: {
    width: '100%',
  },
  panelContent: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  dueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: 4,
  },
  dueCopy: {
    flexShrink: 1,
  },
  eyebrow: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  title: {
    ...typography.headline,
    color: colors.text,
    fontSize: 20,
  },
  sub: {
    ...typography.caption,
    color: colors.textMuted,
  },
  subSoon: {
    color: colors.warning,
    fontWeight: '600',
  },
  kpis: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  kpi: {
    flex: 1,
    backgroundColor: colors.surfaceElevated,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    gap: 2,
  },
  kpiLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  kpiValue: {
    ...typography.body,
    color: colors.text,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  kpiDue: {
    color: colors.warning,
  },
  listContent: {
    gap: 2,
    paddingBottom: spacing.xs,
  },
  sectionTitle: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
  txHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 44,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    cursor: 'pointer',
  },
  rowPressed: {
    opacity: 0.88,
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  rowMain: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  merchant: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  date: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  amount: {
    ...typography.body,
    color: colors.warning,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  amountCredit: {
    color: colors.accent,
  },
  chevron: {
    ...typography.headline,
    color: colors.textMuted,
    fontWeight: '300',
  },
  loader: {
    marginVertical: spacing.md,
  },
  empty: {
    ...typography.body,
    color: colors.textMuted,
    paddingVertical: spacing.sm,
  },
  error: {
    ...typography.caption,
    color: colors.warning,
  },
});
